import type { PartDefinition } from '@rogue/rackbuilder'
import { ANODIZED_COLORS } from '../../constants'
import type { TrapBarOptions } from '../../RackTypes'
import { findBundleProduct } from '../../utils/product-lookup'
import { TrapBarBuilder } from './trapBarBuilder'
import type { IRogue, BuyRequest, Product } from '@/js/services/RogueTypes'
import { getSimpleForBuyRequest } from '@/js/helpers/buy-request'

function getHandleSelections(rogue: IRogue, buyRequest: BuyRequest): (Product | undefined)[] {
  let bundleBuyRequest = buyRequest
  let bundle = rogue.getProductById(bundleBuyRequest.productId)

  while (bundle && bundle.type !== 'bundle' && bundleBuyRequest.children?.[0]) {
    bundleBuyRequest = bundleBuyRequest.children[0]
    bundle = rogue.getProductById(bundleBuyRequest.productId)
  }

  // Both rows are titled "Handle:", so findBundleProduct would return the first twice; the required row is the primary pair.
  const handleOptions = (bundle?.bundleOptions ?? [])
    .filter(opt => String(opt.title ?? '').toLowerCase().includes('handle'))
    .sort((a, b) => Number(b.isRequired) - Number(a.isRequired))

  return handleOptions.map((opt) => {
    const child = bundleBuyRequest.children?.find(c => c.metadata?.bundleOptionId === opt.id && c.qty > 0)

    return child ? getSimpleForBuyRequest(child, rogue) : undefined
  })
}

function getHandleDiameter(handle?: Product): string | undefined {
  const diameter = parseInt(String(handle?.attributes?.grip_diameter ?? ''), 10)

  return Number.isNaN(diameter) ? undefined : String(diameter)
}

export function createTrapBar(rogue: IRogue, buyRequest: BuyRequest): PartDefinition {
  const frame = findBundleProduct(rogue, buyRequest, 'frame', true)
  const edition = String(frame?.attributes?.edition ?? '').toLowerCase()
  const [handle, handleHigh] = getHandleSelections(rogue, buyRequest)

  let brackets: TrapBarOptions['brackets'] = 'dual 4'

  // Matches trap-bar-rules.ts, which only hides the second handle row on an exact 'single'.
  if (edition === 'single') {
    brackets = 'single'
  } else if (edition.includes('6')) {
    brackets = 'dual 6'
  }

  const config: TrapBarOptions = {
    frameType    : String(frame?.attributes?.frame_type ?? '').toLowerCase() === 'open' ? 'open' : 'closed',
    brackets,
    rackable     : String(frame?.attributes?.bar_sleeve_design ?? '').toLowerCase() !== 'non-rackable',
    handle       : getHandleDiameter(handle),
    handleHigh   : getHandleDiameter(handleHigh),
    showEquipped : Boolean(buyRequest.metadata?.showEquipped),
    materials    : {
      handle     : ANODIZED_COLORS[String(handle?.attributes?.handle_color ?? '')],
      handleHigh : ANODIZED_COLORS[String(handleHigh?.attributes?.handle_color ?? '')],
    },
  }

  return new TrapBarBuilder(config).build()
}
